import { FC, useState } from 'react';

import { NormalizedProduct } from '@ravago/shared/page-data/models/contentful-mapped';
import { DisplayOrder, OrderBaseUrls, OrderLineOpenedFrom } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Flyout } from '@ravago/shared/radiance/components/flyout/components/Flyout/Flyout';
import { FlyoutContent } from '@ravago/shared/radiance/components/flyout/components/Flyout/FlyoutContent';
import { FlyoutHeader } from '@ravago/shared/radiance/components/flyout/components/Flyout/FlyoutHeader';
import { Pagination } from '@ravago/shared/radiance/components/pagination/components/Pagination/Pagination';
import { useTranslations } from '@ravago/shared/translations/client';

import { useCallOffAgreementContext } from '../../../context/call-off-agreement-context';
import { useFlyoutContext } from '../../../hooks/use-flyout-context';
import { OrderLineFlyout } from '../order-line-flyout/order-line-flyout';

import { DesktopOrderLinesTable } from './desktop-order-lines-table';
import { MobileOrderLinesTable } from './mobile-order-lines-table';

interface OrderLinesTableProps {
   orderLines: DisplayOrder[];
   isConsumer: boolean;
   locale: string;
   openedFrom: OrderLineOpenedFrom;
   isUSBusinessContext: boolean;
   showInvoiceDocuments: boolean;
   usesAgreementApp?: boolean;
   acceptWebp?: boolean;
   itemsPerPage?: number;
   page: number;
   orderBaseUrls: OrderBaseUrls;
   onPageChange: (page: number) => void;
   requestCoa: (orderNumber: string, producerLotNr: string) => Promise<boolean>;
   requestDeliveryNote: (orderNumber: string, transportOrderLineNr: number, bundledTransportOrderId?: number) => Promise<boolean>;
   requestBol: (orderNumber: string, transportOrderLineNr: number, bundledTransportOrderId?: number) => Promise<boolean>;
   downloadDocument: (downloadEndpoint: string, correlationId: string) => Promise<string | undefined>;
}

export const OrderLinesTable: FC<OrderLinesTableProps> = ({
   orderLines,
   locale,
   itemsPerPage = 5,
   page,
   isConsumer,
   acceptWebp,
   openedFrom,
   usesAgreementApp = false,
   onPageChange,
   requestCoa,
   requestBol,
   requestDeliveryNote,
   downloadDocument,
   isUSBusinessContext,
   showInvoiceDocuments,
   orderBaseUrls
}) => {
   const translate = useTranslations();
   const { dispatch: flyoutDispatch } = useFlyoutContext();
   const { dispatch: callOffAgreementDispatch } = useCallOffAgreementContext();
   const [flyoutOpen, setFlyoutOpen] = useState(false);
   const [orderNumber, setOrderNumber] = useState<string | null>(null);
   const [contentfulProduct, setContentfulProduct] = useState<NormalizedProduct | undefined>(undefined);

   const totalTableItems = itemsPerPage;
   const startIndex = (page - 1) * totalTableItems;
   const endIndex = startIndex + totalTableItems;

   const onOrderLineButtonClick = (orderLine: DisplayOrder) => {
      if (isConsumer && callOffAgreementDispatch) {
         if (orderLine.isCallOff) {
            callOffAgreementDispatch({
               type: 'set_call_off_id',
               value: { callOffId: orderLine.orderNumber }
            });
            callOffAgreementDispatch({
               type: 'set_uses_agreement_app',
               value: { usesAgreementApp }
            });
            flyoutDispatch?.({
               type: 'open_flyout',
               value: { flyoutType: 'call-off-agreement', title: 'Call off agreement' }
            });
            return;
         }
         setOrderNumber(orderLine.orderNumber);
         setContentfulProduct(orderLine.contentfulProduct);
         setFlyoutOpen(true);
      }
   };

   return (
      <>
         <Box margin={{ top: 'md' }} direction="column" gap="sm" width="100%">
            <BreakpointSwitch includeViews={['desktop']}>
               <DesktopOrderLinesTable
                  orderLines={orderLines}
                  acceptWebp={acceptWebp}
                  locale={locale}
                  onOrderLineButtonClick={onOrderLineButtonClick}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  openedFrom={openedFrom}
                  orderBaseUrls={orderBaseUrls}
               />
            </BreakpointSwitch>
            <BreakpointSwitch includeViews={['phone', 'tab-port', 'tab-land']}>
               <MobileOrderLinesTable
                  orderLines={orderLines}
                  locale={locale}
                  onOrderLineButtonClick={onOrderLineButtonClick}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  acceptWebp={acceptWebp}
                  openedFrom={openedFrom}
                  orderBaseUrls={orderBaseUrls}
               />
            </BreakpointSwitch>
            {orderLines.length > totalTableItems && (
               <Box justify="end">
                  <Pagination current={page} onChange={onPageChange} total={Math.ceil(orderLines.length / totalTableItems)} />
               </Box>
            )}
         </Box>
         {orderNumber && (
            <Flyout open={flyoutOpen} closeOnOutsideClick onFlyoutClose={() => setFlyoutOpen(false)}>
               <FlyoutHeader>
                  {translate('page-consumer.order-line-flyout.order')} {orderNumber}
               </FlyoutHeader>
               <FlyoutContent>
                  <OrderLineFlyout
                     locale={locale}
                     isUSBusinessContext={isUSBusinessContext}
                     fullOrderNumber={orderNumber}
                     contentfulProduct={contentfulProduct}
                     openedFrom={openedFrom}
                     orderBaseUrls={orderBaseUrls}
                     showInvoiceDocuments={showInvoiceDocuments}
                     requestCoa={requestCoa}
                     requestBol={requestBol}
                     requestDeliveryNote={requestDeliveryNote}
                     downloadDocument={downloadDocument}
                  />
               </FlyoutContent>
            </Flyout>
         )}
      </>
   );
};
